import type { IntranetMenuBody } from './intranet.types';
import type { ContactBody, LinkedEmailBody, PersonBody, PersonInput } from './entities.types';

export interface UserBody {
    user_id: number;
    username: string;
    /** Never empty: stored, else from the names (`SJa`), else the user id with leading zeros (`007`). */
    acronym: string;
    color: string | null;
    is_active: boolean;
    last_login: string | null;
    last_work: string | null;
    person_id: number | null;
    nickname: string | null;
    titles: string | null;
    given_name: string | null;
    family_name: string | null;
    email: string | null;
    created_at: string | null;
    updated_at: string | null;
    /** GET /persons/{id} and GET /favorites only: the effective teams of the person's user. */
    teams?: TeamBody[];
    /** False when an admin may see but not change this root account. */
    is_editable?: boolean;
}

/** Work the user has to do first (EUserTodo). */
export type UserTodo = 'password_change' | 'tfa_app';

/** Mirrors WhoAmIBody::getAsArray(); `user` only while logged in. The flags are the effective rights (ROOT ⊃ ADMIN ⊃ USER). */
export interface WhoAmIBody {
    isLoggedIn: boolean;
    is_root: boolean;
    is_admin: boolean;
    is_user: boolean;
    /** The record types the user can read at least one record of; a type missing here does not exist for the user. */
    readable: string[];
    /** The intranets the user reaches, each one a point in the menu. */
    intranets: IntranetMenuBody[];
    todo: UserTodo[];
    user?: UserBody;
    tfa_app_deadline?: string | null;
}

/** Mirrors CheckUserBody::getData(); `registerEmail` only when nobody has that name. */
export interface CheckUserBody {
    isValid: boolean;
    registerEmail?: string;
}

/** What the client may show of a started second factor (UserDealer DTfaHint). */
export interface TfaHint {
    method: 'email' | 'app';
    /** The masked recipient or app hint. */
    hint: string;
    /** Seconds since the epoch. */
    expiresAt: number;
}

export interface TfaAppBody {
    is_linked: boolean;
}

export interface MicrosoftAccountBody {
    is_linked: boolean;
    account?: string | null;
}

/** POST /login/microsoft, POST /account/microsoft: where to send the browser. */
export interface MicrosoftAuthorizeBody {
    url: string;
}

/** POST /microsoft/callback: linked (`link`) or logged in (`login`). */
export interface MicrosoftCallbackBody {
    purpose: 'link' | 'login';
    account: string;
    is_logged_in: boolean;
    todo: UserTodo[];
}

export interface TfaAppSetupBody {
    secret: string;
    uri: string;
}

/** Mirrors LoginBody::getAsArray(): `tfa` after step 1, `user` after step 2. */
export interface LoginBody {
    isLoggedIn: boolean;
    tfa?: TfaHint;
    todo: UserTodo[];
    user?: UserBody;
}

/** Mirrors ResetPasswordBody::getAsArray(): `expiresAt` after step 1, `isValid` after 2, `isChanged` after 3. */
export interface ResetPasswordBody {
    isValid: boolean;
    isChanged: boolean;
    /** Seconds since the epoch. */
    expiresAt?: number;
}

/** Mirrors RegisterBody::getAsArray(): `email` and `expiresAt` after step 1, `isActive` after step 2. */
export interface RegisterBody {
    isActive: boolean;
    email?: string;
    /** Seconds since the epoch. */
    expiresAt?: number;
}

/** The writable fields of a user (UserRow::listWritableColumns()); the password never. */
export interface UserInput {
    [key: string]: string | boolean | null | undefined;
    username?: string;
    acronym?: string | null;
    color?: string | null;
    is_active?: boolean;
}

/** The body of a user write: `user`, and `person_id` (an existing person) or `person` — never both. */
export interface UserWrite {
    [key: string]: UserInput | PersonInput | number | undefined;
    user: UserInput;
    person_id?: number;
    person?: PersonInput;
}

/** What every user endpoint answers: the account and its linked person. */
export interface UserData {
    user: UserBody;
    person?: PersonBody;
    /** GET /users only: the effective teams, the implied ones included. */
    teams?: TeamBody[];
}

/** GET/PATCH /account: the own account as stored (`acronym` null when none is stored) and its person. */
export interface AccountBody {
    user: Omit<UserBody, 'acronym'> & { acronym: string | null };
    person?: PersonBody;
    /** The organizations of the person — shown only, the account cannot change them. */
    contacts: ContactBody[];
    tfa_app?: TfaAppBody;
    microsoft?: MicrosoftAccountBody;
    /** GET /account: the effective teams, the implied ones included — shown only. */
    teams?: TeamBody[];
}

/** PATCH /account: only these fields, `person` only with a linked one. */
export interface AccountWrite {
    [key: string]: { [key: string]: string | null | undefined } | undefined;
    user?: { [key: string]: string | null | undefined; acronym?: string | null; color?: string | null };
    person?: PersonInput;
}

/** A team (TeamRow); ids below 42 (USER, ADMIN, ROOT) are built in and cannot be deleted. */
export interface TeamBody {
    team_id: number;
    team: string;
    acronym: string;
    color: string;
    is_protected: boolean;
    /** The users stored in the team (lists and single GET); a used team cannot be deleted. */
    user_count?: number;
    is_used?: boolean;
    created_at: string | null;
    updated_at: string | null;
}

/** GET /teams/{id}: the team and the users stored in it (not the ones a higher team implies). */
export interface TeamDetails {
    team: TeamBody;
    users: UserBody[];
}

/** The writable fields of a team. */
export interface TeamInput {
    [key: string]: string | undefined;
    team?: string;
    acronym?: string;
    color?: string;
}

/** The fixed team ids (ETeam). */
export const TeamIds = { user: 1, admin: 2, root: 3 } as const;

/** GET /users/{id}: plus the addresses of its person, the primary one first, and the user as stored. */
export interface UserDetails extends UserData {
    /** Without what viw_users derives — `acronym` null when none is stored. The base of an edit. */
    stored: Omit<UserBody, 'acronym'> & { acronym: string | null };
    emails: LinkedEmailBody[];
    tfa_app?: TfaAppBody;
    microsoft?: MicrosoftAccountBody;
    /** The effective teams, the implied ones included (ROOT brings ADMIN and USER). */
    teams: TeamBody[];
    /** The stored teams, the base of the team editor. */
    team_ids: number[];
    /** False for a root account while the logged-in user is no root — viewing only. */
    is_editable: boolean;
    /** The logged-in user marked it. */
    is_favorite: boolean;
}
